// Audience tiers (Phase: admin/role-based-access §1). See Phases/admin/PHASE-ROLE-BASED-ACCESS.md.
//
// A tier IS a permission key — never a parallel flag. `TIER_PERMISSION` is the only mapping, so a
// tier check is literally a `can()` call and cannot contradict the server-driven permission map.
//
// The hierarchy needs no code here: the backend's compute_permissions already ORs staff into the
// lower tiers (`is_org_admin = is_staff or …`), so a platform admin's map already carries
// `org:manage_members`. Inheritance is a property of the data, not logic we maintain.
//
// Everything in this file is a pure function or a constant so it can be unit-tested without React;
// the hook wrapper lives in `@/hooks/useAccess`.
import type { PermissionKey } from '@/types';

/** The three audiences, ascending. */
export type Tier = 'user' | 'org' | 'platform';

/** What a page or element declares: a tier (the ladder) or a raw capability key. */
export type AccessSpec = Tier | PermissionKey;

/** The `can()` signature, taken as a parameter so these stay pure. */
export type CanFn = (permission: PermissionKey) => boolean;

/**
 * A tier expressed as the permission key that defines it. `user` is `null` — "merely
 * authenticated", which `ProtectedRoute` already guarantees for every route in this tree.
 */
export const TIER_PERMISSION: Record<Tier, PermissionKey | null> = {
  user: null,
  org: 'org:manage_members',
  platform: 'admin:approve',
};

/** Ascending rank, for "is this tier at least that tier" comparisons. */
export const TIER_RANK: Record<Tier, number> = { user: 0, org: 1, platform: 2 };

/** Human labels for the View-as control (Phase 5). */
export const TIER_LABEL: Record<Tier, string> = {
  user: 'User',
  org: 'Org admin',
  platform: 'Platform admin',
};

/**
 * The lowest tier that legitimately holds each permission — the basis for the View-as mask.
 *
 * Typed as a total `Record<PermissionKey, Tier>` deliberately: adding a `PermissionKey` without
 * classifying it here is a typecheck failure, not a silently unmasked capability.
 */
export const PERMISSION_MIN_TIER: Record<PermissionKey, Tier> = {
  // Any active member holds these — `org:view` and `pe:dataset` are granted to members by
  // compute_permissions, not just to admins.
  'results:export': 'user',
  'org:view': 'user',
  'pe:dataset': 'user',
  // Org owner/admin.
  'org:manage_members': 'org',
  'billing:view': 'org',
  // Quralyst staff, and the two grants deliberately narrower than staff.
  'admin:approve': 'platform',
  'pe:admin': 'platform',
  'pe:batch_import': 'platform',
  'pe:batch_edit_holdings': 'platform',
  'pe:enrichment_chain': 'platform',
};

const TIER_NAMES = Object.keys(TIER_PERMISSION) as Tier[];

/** Narrow an `AccessSpec` to a `Tier`. Tier names and permission keys never collide as strings. */
export function isTier(spec: AccessSpec): spec is Tier {
  return (TIER_NAMES as readonly string[]).includes(spec);
}

/** The highest tier the caller holds. */
export function resolveTier(can: CanFn): Tier {
  if (can('admin:approve')) return 'platform';
  if (can('org:manage_members')) return 'org';
  return 'user';
}

/** Whether `tier` is at least `min`. */
export function tierAtLeast(tier: Tier, min: Tier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[min];
}

/** Whether a permission is one the given tier would hold — the View-as mask predicate (Phase 5). */
export function permissionVisibleAtTier(permission: PermissionKey, tier: Tier): boolean {
  return tierAtLeast(tier, PERMISSION_MIN_TIER[permission]);
}

/**
 * The access decision. `deny` lists tiers excluded from an otherwise-passing spec.
 *
 * **Precondition: the caller is authenticated.** `access: 'user'` maps to `null` (no permission
 * required), so this returns `true` for it unconditionally — it cannot tell a member from a
 * signed-out visitor. Authentication is checked one level up, in `useAccess`, which is the path
 * every real caller takes. Do not call this directly without establishing auth first.
 *
 * **`deny` is cosmetic today.** The backend still grants platform staff cross-tenant org access
 * (see the privacy programme in the backend repo, phases H1 and P). Until those land, a `deny`
 * entry hides a control rather than revoking a capability — use it for clutter, not confidentiality.
 */
export function accessAllows(can: CanFn, spec: AccessSpec, deny?: Tier[]): boolean {
  if (deny?.length && deny.includes(resolveTier(can))) return false;
  if (isTier(spec)) {
    const key = TIER_PERMISSION[spec];
    return key === null ? true : can(key);
  }
  return can(spec);
}
